import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading, Subheading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { areaInputValue, orgAreaUnit } from '../lib/area'
import { useAuth } from '../lib/auth'
import { canadaGapBadgeProps, draftNumber, exportH1, issueFor, useCanadaGapStatus, useEntryCheck } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { useField } from '../lib/field'
import { useFieldSeason } from '../lib/field-seasons'
import { useFarms } from '../lib/farms'
import { ProductPicker } from '../lib/product-picker'
import { useSprayLogs, type CreateSprayLogInput } from '../lib/spray-logs'
import type { AreaUnit, Field, SprayLog } from '../lib/types'
import { Shell } from '../pages/shell'
import { checkBadgeProps, EntryCheckPanel, InputWarning, SavedEntryNotice } from './entry-check'
import { EntryIssues } from './entry-issues'
import { SeasonHeaderDialog } from './season-header-dialog'

const APPLICATION_METHODS = ['ground', 'air', 'furrow', 'seed', 'foliar', 'drip', 'other'] as const
const methodName = (m: string) => (m === 'drip' ? 'Drip / chemigation' : m.charAt(0).toUpperCase() + m.slice(1))

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

/** ISO date plus whole days (UTC, so the day never shifts). */
function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

type ProductRow = {
  productName: string
  /** The registered product picked from the registry; '' when the name was typed. */
  productId: string
  /** For the "linked" line under the name; not saved (the server keeps the registration's own). */
  activeIngredients: string
  pcpNumber: string
  quantityUsed: string
  quantityUnit: string
  rateApplied: string
  rateUnit: string
  labelFollowed: boolean
  phiDays: string
  eahd: string
}

const emptyProduct = (): ProductRow => ({
  productName: '',
  productId: '',
  activeIngredients: '',
  pcpNumber: '',
  quantityUsed: '',
  quantityUnit: 'kg',
  rateApplied: '',
  rateUnit: 'kg/ha',
  labelFollowed: false,
  phiDays: '',
  eahd: '',
})

const toProductRow = (p: NonNullable<SprayLog['products']>[number]): ProductRow => ({
  productName: p.productName,
  productId: p.productId ?? '',
  activeIngredients: '',
  pcpNumber: p.pcpNumber ?? '',
  quantityUsed: String(p.quantityUsed),
  quantityUnit: p.quantityUnit,
  rateApplied: p.rateApplied == null ? '' : String(p.rateApplied),
  rateUnit: p.rateUnit ?? 'kg/ha',
  labelFollowed: p.labelFollowed ?? false,
  phiDays: p.phiDays == null ? '' : String(p.phiDays),
  eahd: p.eahd ?? '',
})

const optionalNumber = (s: string) => (s.trim() === '' ? undefined : Number(s))
const optionalText = (s: string) => s.trim() || undefined

/** Add a spray, or edit `initial`. Captures every CanadaGAP Form H1 column. */
function SprayLogDialog({
  open,
  onClose,
  field,
  initial,
  onSave,
}: {
  open: boolean
  onClose: () => void
  field: Field
  initial?: SprayLog
  onSave: (input: Omit<CreateSprayLogInput, 'fieldId' | 'seasonYear'>) => Promise<unknown>
}) {
  const [appliedOn, setAppliedOn] = useState(initial?.appliedOn ?? '')
  const [applicationMethod, setApplicationMethod] = useState<string>(initial?.applicationMethod ?? 'ground')
  const { org } = useAuth()
  const fieldUnit = orgAreaUnit(org)
  const [areaTreated, setAreaTreated] = useState(initial ? String(initial.areaTreated ?? '') : areaInputValue(field.areaM2, fieldUnit))
  const [areaUnit, setAreaUnit] = useState<AreaUnit>(initial?.areaUnit ?? fieldUnit)
  const [weatherConditions, setWeatherConditions] = useState(initial?.weatherConditions ?? '')
  const [applicatorName, setApplicatorName] = useState(initial?.applicatorName ?? '')
  const [customInvoiceAttached, setCustomInvoiceAttached] = useState(initial?.customInvoiceAttached ?? false)
  const [products, setProducts] = useState<ProductRow[]>(
    initial?.products?.length ? initial.products.map(toProductRow) : [emptyProduct()],
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { farmId } = useParams<{ farmId: string }>()
  // Check the products that will be saved (those with a name); until one is named, check them all.
  // `checkedRows[k]` is the form row of the k-th product sent, so warnings land on the right row.
  const named = products.filter((p) => p.productName.trim())
  const checkedRows = products.flatMap((p, i) => (named.length === 0 || p.productName.trim() ? [i] : []))
  const check = useEntryCheck(org?.id, farmId, {
    form: 'h1',
    entry: {
      areaTreated: draftNumber(areaTreated),
      areaUnit: draftNumber(areaTreated) == null ? null : areaUnit,
      weatherConditions,
      applicatorName,
      customInvoiceAttached,
      products: (named.length ? named : products).map((p) => ({
        productName: p.productName,
        pcpNumber: p.pcpNumber,
        quantityUsed: draftNumber(p.quantityUsed),
        quantityUnit: p.quantityUnit,
        rateApplied: draftNumber(p.rateApplied),
        rateUnit: draftNumber(p.rateApplied) == null ? null : p.rateUnit,
        labelFollowed: p.labelFollowed,
        eahd: p.eahd || null,
        phiDays: draftNumber(p.phiDays),
      })),
    },
  })

  function updateProduct<K extends keyof ProductRow>(index: number, key: K, value: ProductRow[K]) {
    setProducts((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)))
  }

  // Typing the name again unlinks a picked product, and drops the PCP number the pick filled in;
  // a PCP number typed by hand for an unlinked name stays.
  function typeProductName(index: number, productName: string) {
    setProducts((prev) =>
      prev.map((p, i) => (i !== index ? p : p.productId ? { ...p, productName, productId: '', activeIngredients: '', pcpNumber: '' } : { ...p, productName })),
    )
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const validProducts = products.filter((p) => p.productName.trim())
    if (!appliedOn || validProducts.length === 0) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        appliedOn,
        applicationMethod,
        areaTreated: optionalNumber(areaTreated) ?? null,
        areaUnit: optionalNumber(areaTreated) == null ? null : areaUnit,
        weatherConditions: weatherConditions.trim() || null,
        applicatorName: applicatorName.trim() || null,
        customInvoiceAttached,
        products: validProducts.map((p) => ({
          productName: p.productName.trim(),
          productId: p.productId || undefined,
          pcpNumber: optionalText(p.pcpNumber),
          quantityUsed: Number(p.quantityUsed) || 0,
          quantityUnit: p.quantityUnit.trim() || 'kg',
          rateApplied: optionalNumber(p.rateApplied),
          rateUnit: optionalNumber(p.rateApplied) == null ? undefined : optionalText(p.rateUnit),
          labelFollowed: p.labelFollowed,
          phiDays: optionalNumber(p.phiDays),
          eahd: p.eahd || undefined,
        })),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save spray log.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>{initial ? 'Edit spray' : 'Log a spray'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="H1" formName="Agronomic Inputs (Agricultural Chemicals)" {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <Label>Date applied</Label>
                <Input type="date" value={appliedOn} onChange={(e) => setAppliedOn(e.target.value)} required autoFocus />
              </FormField>
              <FormField>
                <Label>Application method</Label>
                <Select value={applicationMethod} onChange={(e) => setApplicationMethod(e.target.value)}>
                  {APPLICATION_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {methodName(m)}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <div className="flex gap-3">
              <FormField className="flex-1">
                <Label>Area treated</Label>
                <Input type="number" min="0" step="0.01" value={areaTreated} onChange={(e) => setAreaTreated(e.target.value)} />
                <InputWarning result={check} input="areaTreated" />
              </FormField>
              <FormField className="w-32">
                <Label>Unit</Label>
                <Select value={areaUnit} onChange={(e) => setAreaUnit(e.target.value as AreaUnit)}>
                  <option value="acre">Acres</option>
                  <option value="hectare">Hectares</option>
                </Select>
              </FormField>
            </div>
            <FormField>
              <Label>Weather conditions</Label>
              <Input
                value={weatherConditions}
                onChange={(e) => setWeatherConditions(e.target.value)}
                placeholder="e.g. Clear, 15°C, wind 5 km/h NW"
              />
              <InputWarning result={check} input="weatherConditions" />
            </FormField>
            <FormField>
              <Label>Applicator name</Label>
              <Input value={applicatorName} onChange={(e) => setApplicatorName(e.target.value)} />
            </FormField>
            <label className="flex items-center gap-3">
              <Checkbox checked={customInvoiceAttached} onChange={setCustomInvoiceAttached} />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Custom application — invoice attached</span>
            </label>
          </FieldGroup>

          <Subheading className="mt-8">Products</Subheading>
          <div className="mt-3 space-y-4">
            {products.map((product, index) => {
              const phi = optionalNumber(product.phiDays)
              const derivedEahd = appliedOn && phi != null && !Number.isNaN(phi) ? addDays(appliedOn, phi) : null
              return (
                <div key={index} className="rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                    <FormField className="sm:col-span-4">
                      <Label>Product / trade name</Label>
                      <ProductPicker
                        name={product.productName}
                        picked={product.productId ? product : null}
                        onName={(name) => typeProductName(index, name)}
                        onPick={(picked) => setProducts((prev) => prev.map((p, i) => (i === index ? { ...p, ...picked } : p)))}
                        placeholder="e.g. Pristine WG"
                        required
                      />
                    </FormField>
                    <FormField className="sm:col-span-2">
                      <Label>PCP #</Label>
                      <Input value={product.pcpNumber} onChange={(e) => updateProduct(index, 'pcpNumber', e.target.value)} />
                      <InputWarning result={check} input="pcpNumber" product={checkedRows.indexOf(index)} />
                    </FormField>
                    <FormField className="sm:col-span-2">
                      <Label>Quantity used</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={product.quantityUsed}
                        onChange={(e) => updateProduct(index, 'quantityUsed', e.target.value)}
                        required
                      />
                      <InputWarning result={check} input="quantityUsed" product={checkedRows.indexOf(index)} />
                    </FormField>
                    <FormField className="sm:col-span-1">
                      <Label>Unit</Label>
                      <Input value={product.quantityUnit} onChange={(e) => updateProduct(index, 'quantityUnit', e.target.value)} />
                    </FormField>
                    <FormField className="sm:col-span-2">
                      <Label>Rate applied</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.0001"
                        value={product.rateApplied}
                        onChange={(e) => updateProduct(index, 'rateApplied', e.target.value)}
                      />
                      <InputWarning result={check} input="rateApplied" product={checkedRows.indexOf(index)} />
                    </FormField>
                    <FormField className="sm:col-span-1">
                      <Label>Per</Label>
                      <Input value={product.rateUnit} onChange={(e) => updateProduct(index, 'rateUnit', e.target.value)} />
                    </FormField>
                    <FormField className="sm:col-span-2">
                      <Label>PHI (days)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={product.phiDays}
                        onChange={(e) => updateProduct(index, 'phiDays', e.target.value)}
                      />
                    </FormField>
                    <FormField className="sm:col-span-4">
                      <Label>Earliest allowable harvest date</Label>
                      <Input type="date" value={product.eahd} onChange={(e) => updateProduct(index, 'eahd', e.target.value)} />
                      {!product.eahd && derivedEahd && (
                        <Text className="mt-1 !text-xs">From PHI: {formatDate(derivedEahd)}</Text>
                      )}
                    </FormField>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <label className="flex items-center gap-3">
                      <Checkbox checked={product.labelFollowed} onChange={(v) => updateProduct(index, 'labelFollowed', v)} />
                      <span className="text-sm text-zinc-700 dark:text-zinc-300">Label instructions followed</span>
                    </label>
                    {products.length > 1 && (
                      <Button plain onClick={() => setProducts((prev) => prev.filter((_, i) => i !== index))}>
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <Button plain onClick={() => setProducts((prev) => [...prev, emptyProduct()])} className="mt-3">
            + Add product (tank mix)
          </Button>
          <EntryCheckPanel result={check} formCode="H1" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="H1" formName="Agronomic Inputs (Agricultural Chemicals)" placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function SprayLogsPage() {
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const { org } = useAuth()
  const { getFarm } = useFarms()
  const currentYear = new Date().getFullYear()
  const [seasonYear, setSeasonYear] = useState(currentYear)
  const [dialog, setDialog] = useState<{ log?: SprayLog } | null>(null)
  const [seasonOpen, setSeasonOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const { field, loading: fieldLoading, error: fieldError } = useField(org?.id, farmId, fieldId)
  const { logs, loading: logsLoading, error: logsError, addLog, updateLog } = useSprayLogs(org?.id, farmId, fieldId, seasonYear)
  const { status, reload: reloadStatus } = useCanadaGapStatus(org?.id, farmId, fieldId, seasonYear)
  const { season, saveSeason } = useFieldSeason(org?.id, farmId, fieldId, seasonYear)

  const farm = farmId ? getFarm(farmId) : undefined
  const backHref = `/farms/${farmId}/fields/${fieldId}`
  const loading = fieldLoading || logsLoading
  const error = fieldError || logsError

  const years = [currentYear - 2, currentYear - 1, currentYear]

  async function handleExport() {
    if (!org?.id || !farmId || !fieldId) return
    setExporting(true)
    setExportError(null)
    try {
      await exportH1(org.id, farmId, fieldId, seasonYear)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setExporting(false)
    }
  }

  async function saveLog(input: Omit<CreateSprayLogInput, 'fieldId' | 'seasonYear'>) {
    if (!fieldId) return
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog({ fieldId, seasonYear, ...input })
    await reloadStatus()
    if (log) setSaved(log.id)
  }

  // The after-save notice is about one entry: clear it when another dialog opens or the season changes.
  useEffect(() => {
    if (dialog) setSaved(null)
  }, [dialog])
  useEffect(() => setSaved(null), [seasonYear])

  return (
    <Shell>
      <Link href={backHref} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {field?.name ?? farm?.name ?? 'Field'}
      </Link>

      {error ? (
        <div className="mt-6 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading || !field ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading>{field.name} — Spray logs</Heading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge
                  framework="CanadaGAP"
                  formCode="H1"
                  formName="Agronomic Inputs (Agricultural Chemicals)"
                  {...canadaGapBadgeProps(status, 'h1')}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={seasonYear} onChange={(e) => setSeasonYear(Number(e.target.value))} className="w-28">
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
              <Button outline onClick={() => setSeasonOpen(true)}>
                Season details
              </Button>
              <Button outline onClick={handleExport} disabled={exporting || logs.length === 0}>
                {exporting ? 'Exporting…' : 'Export H1'}
              </Button>
              <Button href={`/farms/${farmId}/log/spray?field=${fieldId}`}>Log a spray</Button>
            </div>
          </div>

          {exportError && <Text className="mt-3 text-red-600 dark:text-red-400">{exportError}</Text>}
          {saved && status && (
            <SavedEntryNotice formCode="H1" issues={[issueFor(status.forms.h1, saved)].filter((i) => i !== undefined)} onDismiss={() => setSaved(null)} />
          )}

          {logs.length === 0 ? (
            <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
              <Text>No spray logs for {seasonYear}.</Text>
            </div>
          ) : (
            <ul className="mt-8 -mb-8">
              {logs.map((log, index) => (
                <li key={log.id}>
                  <div className="relative pb-8">
                    {index < logs.length - 1 && (
                      <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
                    )}
                    <div className="relative flex gap-4">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10">
                        <span className="size-2 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDialog({ log })}
                        className="-m-2 min-w-0 flex-1 rounded-lg p-2 pt-2.5 text-left hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]"
                      >
                        <p className="text-sm font-semibold text-zinc-950 dark:text-white">{formatDate(log.appliedOn)}</p>
                        <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                          {methodName(log.applicationMethod)} application
                          {log.durationMinutes != null && <> · {log.durationMinutes} min</>}
                          {log.equipmentName && <> · {log.equipmentName}</>}
                          {log.applicatorName && <> · {log.applicatorName}</>}
                          {log.customInvoiceAttached && <> · Custom invoice</>}
                        </p>
                        <ul className="mt-2 space-y-0.5">
                          {(log.products ?? []).length > 0 ? (
                            (log.products ?? []).map((p, i) => (
                              <li key={i} className="text-sm text-zinc-700 dark:text-zinc-300">
                                {p.productName}
                                {p.pcpNumber && <span className="text-zinc-400 dark:text-zinc-500"> (PCP {p.pcpNumber})</span>}
                                <span className="text-zinc-400 dark:text-zinc-500">
                                  {' '}
                                  — {p.quantityUsed} {p.quantityUnit}
                                </span>
                              </li>
                            ))
                          ) : (
                            <li className="text-sm text-zinc-400">No products recorded</li>
                          )}
                        </ul>
                        <EntryIssues issue={issueFor(status?.forms.h1, log.id)} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {dialog && (
            <SprayLogDialog
              key={dialog.log?.id ?? 'new'}
              open
              onClose={() => setDialog(null)}
              field={field}
              initial={dialog.log}
              onSave={saveLog}
            />
          )}
          <SeasonHeaderDialog
            open={seasonOpen}
            onClose={() => setSeasonOpen(false)}
            seasonYear={seasonYear}
            season={season}
            saveSeason={async (input) => {
              await saveSeason(input)
              await reloadStatus()
            }}
          />
        </>
      )}
    </Shell>
  )
}
